/**
 * Service Pipeline Chart
 * Donut chart of services by status (scheduled / in-progress / completed / cancelled).
 * PENDING is intentionally excluded from this breakdown.
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { PieChart as PieIcon, Info } from 'lucide-react'
import type { BreakdownDatum } from '@/types/dashboard'
import { useLocalization } from '@/contexts/LocalizationContext'

// ==================== Colors & labels ====================

// Keyed by the uppercase status produced by mapServiceStatus in api/dashboard.ts.
const STATUS_COLORS: Record<string, string> = {
  PENDING: '#E67E22', // amber
  SCHEDULED: '#497BE8', // blue
  IN_PROGRESS: '#131375', // indigo
  COMPLETED: '#00A67F', // green
  CANCELLED: '#EF4354', // red
}

// Maps the uppercase status to a reusable status.* translation key.
const STATUS_LABEL_KEYS: Record<string, string> = {
  PENDING: 'status.pending',
  SCHEDULED: 'status.scheduled',
  IN_PROGRESS: 'status.inProgress',
  COMPLETED: 'status.completed',
  CANCELLED: 'status.cancelled',
}

// ==================== Types ====================

interface ServicePipelineChartProps {
  data: BreakdownDatum[]
  title?: string
  loading?: boolean
}

// ==================== Component ====================

export function ServicePipelineChart({ data, title, loading = false }: ServicePipelineChartProps) {
  const { t } = useLocalization()

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32 mt-1" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    )
  }

  const labelFor = (status: string) =>
    STATUS_LABEL_KEYS[status] ? t(STATUS_LABEL_KEYS[status]) : status

  const chartData = (data || [])
    .filter((d) => d.name !== 'PENDING')
    .map((d) => ({
      ...d,
      label: labelFor(d.name),
    }))
  const hasData = chartData.some((d) => d.count > 0)

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <PieIcon className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">{title || t('dashboard.servicePipeline')}</CardTitle>
        </div>
        <CardDescription className="mt-1">{t('dashboard.byStatus')}</CardDescription>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Info className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="font-medium text-muted-foreground mb-2">
              {t('dashboard.noDataAvailable')}
            </h3>
          </div>
        ) : (
          <div className="h-64 min-h-[256px] min-w-0 w-full">
            <ResponsiveContainer width="100%" height={256}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="count"
                  nameKey="label"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.name} fill={STATUS_COLORS[entry.name] ?? '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
